<template>
   <div class="position-relative audio-map-wrapper">
      <div id="audio-map" class="w-100 h-100"></div>

      <div :id="`popup-audio-map`" class="ol-popup">
            <a href="#" id="popup-closer-audio-map" class="ol-popup-closer" @click.prevent="hidePopup"></a>
            <div id="popup-content" v-if="popup" class="d-flex flex-column">
                <h5 id="popup-title">{{ popup.title }}</h5>
                <div class="audio-map-popup-audio-controls">
                  <button class="play" @click="onPopupAudioClick(popup.audio)">
                    <i class="fas" :class="currentlyPlaying ? 'fa-pause' : 'fa-play'"></i>
                  </button>
                  <div class="info">
                    <div>
                      {{ popup.subtitle }} del {{ popup.part }}/{{ geoJson.features.length }}
                    </div>
                    <a href="#" @click.prevent="onPopupAudioClick(popup.audio)">
                      Spela upp ljud
                    </a>
                  </div>
                </div>
            </div>
        </div>
   </div>
</template>

<script>
/* global Bus */
import Map from 'ol/Map'
import View from 'ol/View'
import Overlay from 'ol/Overlay'
import VectorSource from 'ol/source/Vector'
import { Vector as VectorLayer } from 'ol/layer'
import { fromLonLat } from 'ol/proj'
import { Icon, Style } from 'ol/style'
import GeoJSON from 'ol/format/GeoJSON'
import wmtsLayer from '../map-component/layers/wmtsLayer'
import geoLocationLayer from '../map-component/layers/geoLocationLayer'
import store from '../../store'
import { mapState } from 'vuex'

export default {
  props: ['geoJson'],

  data () {
    return {
      map: null,
      currentLocation: null,
      markerLayer: null,
      popupOverlay: null,
      popup: null
    }
  },

  async mounted () {
    await store.restored

    const tileLayer = await wmtsLayer()

    this.map = new Map({
      target: 'audio-map',
      layers: [
        tileLayer
      ],
      view: new View({
        center: fromLonLat([15.6213095, 59.7022645]),
        zoom: 8
      })
    })

    this.markerLayer = this.setupMarkerLayer()
    this.map.addLayer(this.markerLayer)
    this.map.addLayer(geoLocationLayer(this.map.getView().getProjection(), pos => this.$store.dispatch('setGeoLocation', pos)))

    this.setupPopup()

    const extent = isFinite(this.markerLayer.getSource().getExtent()[0])
      ? this.markerLayer.getSource().getExtent() : null
    this.fitMapToExtent(extent, 500, 0.2)
  },

  methods: {
    setupMarkerLayer () {
      const _this = this
      const markerLayer = new VectorLayer({
        className: 'markerLayer',
        zIndex: 5,
        source: new VectorSource({
          format: new GeoJSON(),
          features: new GeoJSON().readFeatures(this.geoJson)
        }),
        style: function (feature) {
          const featureIsCurrent = _this.currentLocation ? feature.getId() === _this.currentLocation.id : false
          const bgColor = featureIsCurrent ? '%23288281' : '%23BE3700'
          return new Style({
            zIndex: featureIsCurrent ? 10 : 5,
            image: new Icon({
              anchor: [60, 120],
              anchorXUnits: 'pixels',
              anchorYUnits: 'pixels',
              size: [120, 120],
              scale: featureIsCurrent ? 0.6 : 0.4,
              src: `https://cdn.mapmarker.io/api/v1/pin?size=120&background=${bgColor}&icon=fa-circle&color=%23FFFFFF&voffset=0&hoffset=1`
            })
          })
        }
      })

      return markerLayer
    },

    setupPopup () {
      const container = document.getElementById('popup-audio-map')
      const closer = document.getElementById('popup-closer-audio-map')

      const overlay = new Overlay({
        id: 'popup-audio-map',
        element: container,
        autoPan: false,
        autoPanAnimation: {
          duration: 250
        }
      })

      this.map.addOverlay(overlay)

      const showPopup = (feature) => {
        const coordinates = feature.getGeometry().getCoordinates()
        overlay.setPosition(coordinates)

        const audio = feature.getProperties().audio[0]

        this.popup = {
          feature,
          title: feature.values_.title,
          subtitle: audio.title,
          part: feature.values_.part,
          audio: audio,
          isPlaying: audio.id === this.currentTrackId
        }

        overlay.setPosition(coordinates)
      }

      this.map.on('singleclick', function (event) {
        const feature = this.forEachFeatureAtPixel(event.pixel, function (feature) {
          return feature
        })

        if (feature) {
          showPopup(feature)
        } else {
          this.popup = null
          overlay.setPosition(undefined)
          closer.blur()
        }
      })
    },

    hidePopup: function () {
      const overlay = this.map.getOverlayById('popup-audio-map')
      overlay && overlay.setPosition(undefined)
    },

    onPopupAudioClick (audio) {
      this.$store.dispatch('setPlayStateTrackId', audio.id)
      this.$store.dispatch('setPlayStatePlaying', true)
    },

    fitMapToExtent: function (extent = null, duration = 250, minResolution = 0.3) {
      this.$nextTick(function () {
        const mExtent = extent || this.getExtentIfFinite(this.currentExtent) || this.autoExtent

        if (!mExtent) {
          return
        }

        this.map.getView().fit(mExtent, {
          padding: [100, 100, 100, 100],
          minResolution,
          duration
        })
      })
    }
  },

  watch: {
    currentTrackId: {
      handler: function (value) {
        const feature = this.geoJson.features.find(feature => feature.properties.audio.find(audio => audio.id === value))
        this.currentLocation = feature

        this.$nextTick(function () {
          if (this.markerLayer) {
            this.markerLayer.getSource().clear()
            this.markerLayer.getSource().addFeatures(new GeoJSON().readFeatures(this.geoJson))
          }
        })
      },
      immediate: true
    }
  },

  computed: {
    ...mapState({
      currentTrackId: state => state.audio.playState.trackId
    })
  }
}
</script>
